/**
 * Vocabulário central do catálogo de papéis (T43, PADRONIZACAO-ROLES.md).
 *
 * Só nomes e tipos — nenhuma política, nenhuma concessão. O catálogo em si
 * (`PAPEIS_<Dominio>`, com `concessoes` reais) nasce na Fase 7 (T51-T54), um
 * arquivo por domínio; este arquivo é o único conteúdo compartilhado entre
 * eles, conforme a decisão 8 de `PADRONIZACAO-ROLES.md`.
 */

/**
 * Os 17 domínios canônicos. Lista fechada; domínio novo é PR aqui.
 *
 * `cre` (rótulo de UI do grupo de rotas de Projetos de Engenharia,
 * `src/router/rotas/ProjetosEngenharia.ts`) não é domínio próprio — colapsa em
 * `projetos` (PADRONIZACAO-ROLES.md §3).
 */
export const DOMINIOS = [
  "obra",
  "rdo",
  "materiais",
  "contratos",
  "juridico",
  "sgi",
  "seguros",
  "posvendas",
  "produto",
  "taf",
  "projetos",
  "projetos_fabrica",
  "fabricacao",
  "automacao",
  "field",
  "biblioteca",
  "acesso",
] as const;
export type Dominio = (typeof DOMINIOS)[number];

/**
 * Sujeitos CASL (`subject`) hoje em uso nos arquivos de política do kernel.
 * Levantado por grep sobre `src/shared/autorizacao/*.ts` (T43) — não é
 * aspiracional, é o que já existe.
 */
export const RECURSOS = [
  "all",
  "AUTOMACAO",
  "BIBLIOTECA",
  "CONTRATO_AUTOMACAO",
  "Contratos",
  "DIARIO_OS",
  "DiarioOs",
  "Diretorio",
  "EapOS",
  "FABRICACAO",
  "FIELD",
  "Materiais",
  // T49: criar material por obra e criar por projeto sao cadastros distintos
  // (decisao do usuario, AD-024). A origem vira RECURSO, e nao sufixo no nome da
  // acao — `create_obra`/`create_projeto` tinham o mesmo defeito de `upload_dp`.
  "MateriaisObra",
  "MateriaisProjeto",
  "OS_AUTOMACAO",
  "OS_FIELD",
  "Obra",
  "PEDIDO_AUTOMACAO",
  "PERFIL",
  "POSVENDAS",
  "PROJETO",
  "PROJETO_FABRICA",
  "Permissao",
  "ProcessoJuridico",
  "Produto",
  "RDO",
  "SEGUROS",
  "TAF",
  "TicketInteracao",
  "TicketSgi",
] as const;
export type Recurso = (typeof RECURSOS)[number];

/**
 * Vocabulário de ações CASL (`action`) hoje em uso. Levantado por grep sobre
 * `src/shared/autorizacao/*.ts` (T43).
 */
export const ACOES = [
  "Interagir",
  "aceitaInteracao",
  "adicionar_cliente",
  "aprovar",
  "aviso-atraso",
  // O grep de T43 perdeu esta: `cliente.ts:11,15` concede `comentario` em
  // `PROJETO` ao projetista cliente. Sem ela, `projetos.leitura_externa` — o papel
  // de leitura de cliente que a D4 do AD-031 pediu — nao compila (T56).
  "comentario",
  "config",
  "create",
  "data_planejada",
  "delete",
  "edit",
  "editar",
  "editarpcp",
  "finalizar",
  "gerenciar",
  "inspect",
  "liberar",
  "manage",
  "novaPasta",
  "read",
  "readAcoes",
  "readInterno",
  "seeAll",
  "update",
  "update_status",
  "upload",
  "upload_dp",
  "upload_seg",
  "write",
] as const;
export type Acao = (typeof ACOES)[number];

/**
 * Quanto acesso o papel dá num domínio.
 *
 * Os três primeiros são cumulativos: `admin ⊃ escrita ⊃ leitura`. Os dois últimos
 * são **qualificados** — não entram na escada, e existem porque a escada de três
 * não distingue duas capacidades sobre o mesmo recurso (`ADR-0004 §D3`).
 *
 * - **`escrita_propria`** (AD-031 D5): escreve só onde a pessoa é a relação, e num
 *   conjunto de estados próprio. O caso que a criou: o site manager que é gestor
 *   edita contrato em "Aprovado", enquanto o gestor comum edita em "Emitido para o
 *   Jurídico". Somar as duas em `contratos.leitura` daria o estado "Aprovado" a
 *   **todo** gestor.
 * - **`leitura_externa`** (AD-031 D4): a leitura do cliente, sem `readInterno`.
 *   `projetos.leitura` dá `read` **e** `readInterno` — a leitura interna do
 *   projetista Gali (`projetos.ts:12`) —, e os grupos de cliente a carregavam
 *   inteira. É o vazamento que a D4 fechou.
 *
 * A nota anterior dizia "nunca se adiciona um nível novo". Ela vale para a
 * escada; os dois qualificados são o quarto e o quinto valor que o `ADR-0004 §D3`
 * já previa, e cada um nasceu de uma decisão nomeada, não de conveniência.
 */
export const NIVEIS = [
  "leitura",
  "escrita",
  "admin",
  "escrita_propria",
  "leitura_externa",
] as const;
export type Nivel = (typeof NIVEIS)[number];

/**
 * Conjunto fechado e aditivo — o que não responde "quanto" e sim "o que
 * mais". Adicionar uma capacidade exige entrada aqui (PADRONIZACAO-ROLES.md
 * §1.1) e passa por revisão; o teste de invariante (T45) reprova o nome antes
 * da revisão humana.
 *
 * `total` foi adicionada durante a T44: `acesso_total` (§4.1) renomeia para
 * `acesso.total`, o domínio meta que administra o catálogo inteiro (§3.3) —
 * sem essa entrada a gramática recusaria o único papel do domínio `acesso`
 * hoje em uso.
 *
 * `recebimento` entrou na adequação de Materiais (T33, MAT-17): registrar o recebimento
 * do material na obra (data real, nota fiscal, observação), o `editarObra` do `cbb62e14`.
 */
export const CAPACIDADES = [
  "upload",
  "aprovacao",
  "config",
  "liberacao",
  "criacao",
  "apoio_os",
  "total",
  "recebimento",
] as const;
export type Capacidade = (typeof CAPACIDADES)[number];

/**
 * `<dominio>.<nivel|capacidade>` — a gramática do papel (PADRONIZACAO-ROLES.md
 * §1). Derivado do `as const` acima, não uma string solta: qualquer domínio
 * ou nível/capacidade fora do vocabulário falha em `npm run tsc`.
 */
export type NomePapel = `${Dominio}.${Nivel | Capacidade}`;

/**
 * Uma concessão do catálogo — o que um papel efetivamente libera.
 *
 * `exigeRelacao` e `estados` marcam concessão condicional: quem só tem essas
 * (sem outra concessão incondicional para a mesma ação/recurso) não passa no
 * gate de módulo (`podeNoModulo`) — ver ADR-0003 e P1-5 do spec.
 */
export interface Concessao {
  acoes: Acao[];
  recurso: Recurso;
  /** Papel só concede se o usuário for a relação declarada no registro (ex.: site manager da obra). */
  exigeRelacao?: string[];
  /** Papel só concede quando o registro está num dos estados listados. */
  estados?: string[];
  /** Condição adicional livre, aplicada ao registro (ex.: `{ origem: "obra" }`). */
  condicao?: Record<string, unknown>;
}

/** Um papel do catálogo: domínio, nível (ou capacidade) e as concessões que ele libera. */
export interface Papel {
  dominio: Dominio;
  nivel: Nivel | Capacidade;
  concessoes: Concessao[];
}
